// Utility functions for table data processing

// Helper function to parse comma-separated numbers
export const parseNumber = str => {
  if (str === undefined || str === null || str === '') {
    return 0;
  }
  if (typeof str === 'number') {
    return str;
  }
  if (typeof str !== 'string') {
    return 0;
  }
  return parseFloat(str.replace(/,/g, '')) || 0;
};

// Helper function to format numbers with commas
export const formatNumber = num => num.toLocaleString('en-US', { maximumFractionDigits: 2 });

// Calculate totals for picker groups monitor
export const calculatePickerGroupsTotals = data => {
  if (!data || data.length === 0) {
    return {
      groupName: 0,
      blockName: '',
      totalUnits: '0',
      weight: '0',
      totalWorkHours: '0',
      activeWorkers: '0',
      averagePerWorker: '',
      averageHourlyRate: '',
      isTotal: true
    };
  }

  const totals = data.reduce(
    (acc, row) => {
      acc.totalUnits += parseNumber(row.totalUnits);
      acc.weight += parseNumber(row.weight);
      acc.totalWorkHours += parseNumber(row.totalWorkHours);
      acc.activeWorkers += parseNumber(row.activeWorkers);
      return acc;
    },
    {
      totalUnits: 0,
      weight: 0,
      totalWorkHours: 0,
      activeWorkers: 0
    }
  );

  return {
    groupName: data.length,
    blockName: '',
    totalUnits: formatNumber(totals.totalUnits),
    weight: formatNumber(totals.weight),
    totalWorkHours: formatNumber(totals.totalWorkHours),
    activeWorkers: formatNumber(totals.activeWorkers),
    averagePerWorker: '',
    averageHourlyRate: '',
    isTotal: true
  };
};

// Calculate totals for picker monitor
export const calculatePickerTotals = data => {
  if (!data || data.length === 0) {
    return {
      workerId: 0,
      workerName: '',
      blockName: '',
      groupName: '',
      totalUnits: '0',
      totalWorkHours: '0',
      averageRatePerHour: '',
      averageRatePerDay: '',
      workerComments: '',
      subBlock: '',
      isTotal: true
    };
  }

  const totals = data.reduce(
    (acc, row) => {
      acc.totalUnits += parseNumber(row.totalUnits);
      acc.totalWorkHours += parseNumber(row.totalWorkHours);
      return acc;
    },
    {
      totalUnits: 0,
      totalWorkHours: 0
    }
  );

  return {
    workerId: data.length,
    workerName: '',
    blockName: '',
    deviceGroupName: '',
    totalUnits: formatNumber(totals.totalUnits),
    totalWorkHours: formatNumber(totals.totalWorkHours),
    averageRatePerHour: '',
    averageRatePerDay: '',
    workerComments: '',
    subBlock: '',
    isTotal: true
  };
};

// Calculate totals for block monitor
export const calculateBlockTotals = data => {
  if (!data || data.length === 0) {
    return {
      blockName: 0,
      subBlock: '',
      crop: '',
      variety: '',
      totalScans: '0',
      totalUnits: '0',
      weight: '0',
      kgPerPlant: '0.000',
      kgPerM2: '0.000',
      isTotal: true
    };
  }

  const totals = data.reduce(
    (acc, row) => {
      acc.totalScans += parseNumber(row.totalScans);
      acc.totalUnits += parseNumber(row.totalUnits);
      acc.weight += parseNumber(row.weight);
      return acc;
    },
    {
      totalScans: 0,
      totalUnits: 0,
      weight: 0
    }
  );

  // Calculate average KG/plant and KG/m² with safety checks
  const avgKgPerPlant =
    data.reduce((sum, row) => {
      const kgPerPlant = parseFloat(row.kgPerPlant) || 0;
      return sum + kgPerPlant;
    }, 0) / data.length;

  const avgKgPerM2 =
    data.reduce((sum, row) => {
      const kgPerM2 = parseFloat(row.kgPerM2) || 0;
      return sum + kgPerM2;
    }, 0) / data.length;

  return {
    blockName: data.length,
    subBlock: '',
    crop: '',
    variety: '',
    totalScans: formatNumber(totals.totalScans),
    totalUnits: formatNumber(totals.totalUnits),
    weight: formatNumber(totals.weight),
    kgPerPlant: avgKgPerPlant.toFixed(3),
    kgPerM2: avgKgPerM2.toFixed(3),
    isTotal: true
  };
};

// Calculate totals for agricultural actions
export const calculateAgroActionsTotals = data => {
  const totals = data.reduce(
    (acc, row) => {
      const units = parseNumber(row.units);
      const percentage = parseInt(row.percentageCompletion) || 0;

      acc.totalUnits += units;
      acc.weightedPercentage += units * percentage;
      return acc;
    },
    {
      totalUnits: 0,
      weightedPercentage: 0
    }
  );

  // Calculate weighted average percentage
  const totalPercentage =
    totals.totalUnits > 0 ? Math.round(totals.weightedPercentage / totals.totalUnits) : 0;

  return {
    blockName: `#${data.length}`,
    activityName: '',
    units: formatNumber(totals.totalUnits),
    percentageCompletion: `${totalPercentage}%`,
    isTotal: true
  };
};

// Calculate totals for packer groups monitor
export const calculatePackerGroupsTotals = data => {
  if (!data || data.length === 0) {
    return {
      groupName: 0,
      totalPacked: '0',
      workerCount: '0',
      avgWeight: '0',
      percentageOfTotal: '',
      avgPerWorker: '',
      isTotal: true
    };
  }

  const totals = data.reduce(
    (acc, row) => {
      acc.totalPacked += parseNumber(row.totalPacked);
      acc.workerCount += parseNumber(row.workerCount);
      acc.avgWeight += parseNumber(row.avgWeight);
      return acc;
    },
    {
      totalPacked: 0,
      workerCount: 0,
      avgWeight: 0
    }
  );

  return {
    groupName: `#${data.length}`,
    totalPacked: formatNumber(totals.totalPacked),
    workerCount: formatNumber(totals.workerCount),
    avgWeight: formatNumber(totals.avgWeight / data.length),
    percentageOfTotal: '100%',
    avgPerWorker: formatNumber(totals.totalPacked / totals.workerCount),
    isTotal: true
  };
};

// Calculate totals for packer monitor
export const calculatePackerTotals = data => {
  if (!data || data.length === 0) {
    return {
      workerId: 0,
      workerName: '',
      groupName: '',
      totalPacked: '0',
      percentageOfTotal: '',
      averagePacked: '',
      isTotal: true
    };
  }

  const totals = data.reduce(
    (acc, row) => {
      acc.totalPacked += parseNumber(row.totalPacked);
      return acc;
    },
    {
      totalPacked: 0
    }
  );

  return {
    workerId: `#${data.length}`,
    workerName: '',
    groupName: '',
    totalPacked: formatNumber(totals.totalPacked),
    percentageOfTotal: '100%',
    averagePacked: formatNumber(totals.totalPacked / data.length),
    isTotal: true
  };
};

export const calculatePickingTotals = data => {
  if (!data || data.length === 0) {
    return {
      blockName: 0,
      containerName: '',
      totalCredits: '0',
      totalContainers: '0',
      date: '',
      isTotal: true
    };
  }

  const totals = data.reduce(
    (acc, row) => {
      acc.totalCredits += parseNumber(row.totalCredits);
      acc.totalContainers += parseNumber(row.totalContainers);
      return acc;
    },
    {
      totalCredits: 0,
      totalContainers: 0
    }
  );

  return {
    blockName: data.length,
    containerName: '',
    totalCredits: formatNumber(totals.totalCredits),
    totalContainers: formatNumber(totals.totalContainers),
    date: '',
    isTotal: true
  };
};

export const calculatePackingTotals = data => {
  if (!data || data.length === 0) {
    return {
      containerName: '',
      containerMaterial: '',
      stickerType: '',
      totalPacked: '0',
      packerCount: '0',
      isTotal: true
    };
  }

  const totals = data.reduce(
    (acc, row) => {
      acc.totalPacked += parseNumber(row.totalPacked);
      acc.packerCount += parseNumber(row.packerCount);
      return acc;
    },
    {
      totalPacked: 0,
      packerCount: 0
    }
  );

  return {
    containerName: data.length,
    containerMaterial: '',
    stickerType: '',
    totalPacked: formatNumber(totals.totalPacked),
    packerCount: formatNumber(totals.packerCount),
    isTotal: true
  };
};

export const calculateAgroGroupsTotals = data => {
  if (!data || data.length === 0) {
    return {
      groupName: 0,
      blockName: 0,
      totalUnits: '0',
      weight: '0',
      totalWorkHours: '0',
      activeWorkers: '0',
      averagePerWorker: '0',
      averageHourlyRatePerWorker: '0',
      isTotal: true
    };
  }

  const totals = data.reduce(
    (acc, row) => {
      acc.totalUnits += parseNumber(row.totalUnits);
      acc.weight += parseNumber(row.weight);
      acc.totalWorkHours += parseNumber(row.totalWorkHours);
      acc.activeWorkers += parseNumber(row.activeWorkers);
      return acc;
    },
    {
      totalUnits: 0,
      weight: 0,
      totalWorkHours: 0,
      activeWorkers: 0
    }
  );

  return {
    groupName: `#${data.length}`,
    blockName: '',
    totalUnits: formatNumber(totals.totalUnits),
    weight: formatNumber(totals.weight),
    totalWorkHours: formatNumber(totals.totalWorkHours),
    activeWorkers: formatNumber(totals.activeWorkers),
    averagePerWorker: formatNumber(totals.totalUnits / totals.activeWorkers),
    averageHourlyRatePerWorker: formatNumber(totals.totalUnits / totals.totalWorkHours),
    isTotal: true
  };
};
